import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const heroImages = [
  "https://images.unsplash.com/photo-1573496130141-209d200cebd8?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1585240975858-7264fd020798?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1590650153855-d9e808231d41?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
];

const defaultStats = [
  { label: "Curated Businesses", value: "2,500+" },
  { label: "Diverse Categories", value: "50+" },
  { label: "Cities Represented", value: "200+" },
  { label: "Business Connections", value: "10,000+" },
];

const formatNum = (n) => {
  if (n >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k+`;
  return `${n}+`;
};

export default function Hero({ onJoin, onExplore }) {
  const [stats, setStats] = useState(defaultStats);

  useEffect(() => {
    axios
      .get(`${API}/stats`)
      .then((r) => {
        const d = r.data;
        setStats([
          { label: "Curated Businesses", value: d.curated_businesses.toLocaleString() + "+" },
          { label: "Diverse Categories", value: d.diverse_categories + "+" },
          { label: "Cities Represented", value: d.cities_represented + "+" },
          { label: "Business Connections", value: formatNum(d.business_connections) },
        ]);
      })
      .catch(() => {});
  }, []);

  return (
    <section
      id="hero"
      className="relative overflow-hidden pt-10 pb-24 md:pt-16 md:pb-32"
      data-testid="hero-section"
    >
      {/* Decorative background */}
      <div className="absolute top-24 -left-20 w-96 h-96 rounded-full bg-brand-paper2/60 blur-3xl -z-10" />
      <div className="absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full bg-[#F5D9DE]/40 blur-3xl -z-10" />
      <div className="absolute top-40 right-16 w-24 h-24 decor-dots rounded-full hidden md:block" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Left content */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="lg:col-span-7 relative"
        >
          <div className="inline-flex items-center gap-2 bg-brand-cream border border-brand-line rounded-full px-4 py-1.5 mb-8" data-testid="hero-badge">
            <Sparkles size={14} className="text-brand-wine" />
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-wine">
              A trusted network for women-led business
            </span>
          </div>

          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-medium tracking-[-0.01em] leading-[1.05] text-brand-ink">
            Yuukke <br />
            <span className="italic text-gradient-signature">Business Exchange</span>
          </h1>

          <p className="mt-6 text-lg md:text-xl text-brand-muted max-w-2xl leading-relaxed font-serif italic">
            Discover. Connect. Source from women-led businesses.
          </p>

          <p className="mt-5 text-[15px] md:text-base text-brand-muted max-w-xl leading-relaxed">
            A curated network of verified suppliers and professionals — helping
            organizations, buyers, procurement teams, and partners source with
            confidence.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <button
              onClick={onExplore}
              className="group inline-flex items-center gap-2 bg-brand-wine text-white rounded-lg pl-7 pr-6 py-4 font-semibold text-[15px] transition-all duration-500 hover:bg-brand-wineDark hover:shadow-lift hover:-translate-y-0.5"
              data-testid="hero-explore-btn"
            >
              Explore Businesses
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
            <button
              onClick={onJoin}
              className="inline-flex items-center gap-2 bg-transparent border border-brand-wine text-brand-wine rounded-lg px-7 py-4 font-semibold text-[15px] transition-all duration-500 hover:bg-brand-wine hover:text-white hover:-translate-y-0.5"
              data-testid="hero-join-btn"
            >
              Join the Business Exchange
            </button>
          </div>

          {/* Stats */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-4 max-w-3xl" data-testid="hero-stats">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + i * 0.08 }}
                className="flex flex-col border-t border-brand-line pt-4"
              >
                <span className="font-serif text-3xl md:text-4xl font-medium text-brand-ink leading-none">
                  {s.value}
                </span>
                <span className="mt-2 text-xs md:text-sm text-brand-muted uppercase tracking-widest">
                  {s.label}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Right: portrait collage */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="lg:col-span-5 relative h-[520px] md:h-[600px] hidden md:block"
        >
          <div className="absolute -top-2 right-8 w-72 h-96 rounded-[999px] overflow-hidden shadow-[0_30px_60px_-30px_rgba(42,27,31,0.35)] border-4 border-white">
            <img src={heroImages[0]} alt="Woman entrepreneur" className="w-full h-full object-cover" />
          </div>
          <div className="absolute top-52 -left-4 w-56 h-56 rounded-full overflow-hidden shadow-[0_30px_60px_-30px_rgba(42,27,31,0.35)] border-4 border-white animate-float-slow">
            <img src={heroImages[1]} alt="Woman professional" className="w-full h-full object-cover" />
          </div>
          <div className="absolute bottom-0 right-24 w-52 h-52 rounded-full overflow-hidden shadow-[0_30px_60px_-30px_rgba(42,27,31,0.35)] border-4 border-white">
            <img src={heroImages[2]} alt="Woman founder" className="w-full h-full object-cover" />
          </div>
          <div className="absolute top-6 -right-6 w-16 h-16 rounded-full bg-brand-wine/10 border border-brand-wine/20" />
          <div className="absolute bottom-16 left-4 w-8 h-8 rounded-full bg-brand-wine" />
        </motion.div>
      </div>
    </section>
  );
}
